/**
 * Cloud-provider selector — the shared "tiles in the middle" control.
 *
 * Cost Explorer and Recommendations each carried a byte-identical copy of this
 * markup, and the Dashboard had a dropdown doing the same job with a different
 * shape. One component means the provider selector looks and behaves the same
 * on every page that has one, which is the whole point.
 *
 * Deliberately buttons rather than a dropdown: there are only ever four
 * choices, they are the top-level filter for the whole page, and a dropdown
 * hides three of them behind a click for no benefit.
 */

import { CloudProviderIcon } from './CloudProviderIcon';
import { cn } from '../../utils/cn';

export const PROVIDER_TILES: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'All Providers' },
  { id: 'aws', label: 'AWS' },
  { id: 'azure', label: 'Azure' },
  { id: 'gcp', label: 'GCP' },
];

export interface ProviderTilesProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  /** Horizontal alignment of the row. Centred everywhere today. */
  align?: 'center' | 'start';
  className?: string;
}

export function ProviderTiles<T extends string = string>({
  value,
  onChange,
  align = 'center',
  className,
}: ProviderTilesProps<T>) {
  return (
    <div
      className={cn('flex flex-wrap gap-2', align === 'center' ? 'justify-center' : 'justify-start', className)}
      role="group"
      aria-label="Filter by cloud provider"
    >
      {PROVIDER_TILES.map((tile) => {
        const isActive = value === tile.id;
        return (
          <button
            key={tile.id}
            type="button"
            onClick={() => onChange(tile.id as T)}
            aria-pressed={isActive}
            className={cn(
              'flex h-9 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors',
              isActive
                ? 'border-primary/30 bg-primary/10 text-primary'
                : 'border-border bg-card text-muted-foreground hover:bg-secondary hover:text-foreground'
            )}
          >
            <CloudProviderIcon provider={tile.id} size={16} />
            {tile.label}
          </button>
        );
      })}
    </div>
  );
}
